import { useFetcher } from "~/hooks/useFetcher";
import {
  type PartialSkill,
  SKILL_LIST_LIMIT,
  type SkillPage,
  skillsListPath,
} from "~/services/skills";

const systemKey = skillsListPath({ scope: "SYSTEM", limit: SKILL_LIST_LIMIT });
const userKey = skillsListPath({ scope: "USER", limit: SKILL_LIST_LIMIT });

export interface LibrarySkill extends PartialSkill {
  origin: "system" | "user";
}

interface UseSkillLibraryResult {
  skills: LibrarySkill[];
  isLoading: boolean;
}

/**
 * Issues two parallel `GET /skills/?scope=SYSTEM` and `?scope=USER` calls and
 * merges the responses deduplicated by `id`. SYSTEM entries win on duplicate id.
 *
 * @lat: [[change/agent-skills-migration/api.md#Library reads]]
 */
export function useSkillLibrary(): UseSkillLibraryResult {
  const { data: systemPage, isLoading: systemLoading } = useFetcher<SkillPage>(systemKey);
  const { data: userPage, isLoading: userLoading } = useFetcher<SkillPage>(userKey);

  const merged = new Map<string, LibrarySkill>();
  for (const s of userPage?.items ?? []) {
    merged.set(s.id, { ...s, origin: "user" });
  }
  for (const s of systemPage?.items ?? []) {
    merged.set(s.id, { ...s, origin: "system" });
  }

  return {
    skills: Array.from(merged.values()),
    isLoading: systemLoading || userLoading,
  };
}
